import ActionTextIcon from "@/components/icons/ActionTextIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatPhoneNumber } from "@/utils/formatUtils";
import { URLQuery } from "@/utils/queryUtils";
import { numberOrZero } from "@/utils/utils";
import { Space, Table, Tag } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router";
import { useAdminFilterUsers } from "../../../../../common/api/queries";
import { TableSizes } from "../../../../../common/constants";
import { SearchPageRequest } from "../../../../../common/types";
import { ADMIN_USER_ROUTE_PATHS } from "../../../paths";
import { UserAdminView } from "../../../types";
import { hasConfirmedAgentUserAccount, hasDisabledAgentUserAccountsOnly } from "../../../utils";

type Props = {
  searchParams: SearchPageRequest;
};

export const AdminUserListTableView = ({ searchParams }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { data, isLoading } = useAdminFilterUsers(searchParams);

  const columns: ColumnsType<UserAdminView> = [
    {
      key: "name",
      title: t("user.attrs.name"),
      width: 200,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.name}</Ellipsis>,
    },
    {
      key: "email",
      title: t("user.attrs.email"),
      width: 200,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.email || record.emailToConfirm}</Ellipsis>,
    },
    {
      key: "phone",
      title: t("user.attrs.phone"),
      width: 160,
      ellipsis: { showTitle: false },
      render: (_, record) => (
        <Ellipsis>
          {formatPhoneNumber(
            hasConfirmedAgentUserAccount(record) ? (record.phone ?? "") : (record.phoneToConfirm ?? ""),
          )}
        </Ellipsis>
      ),
    },
    {
      key: "tags",
      width: 500,
      render: (_, record) => (
        <Space>
          {record.locked && (
            <Tag color="red" style={{ marginRight: 0 }}>
              {t("user.helpers.lockedUser")}
            </Tag>
          )}
          {hasDisabledAgentUserAccountsOnly(record) && <Tag color="purple">{t("user.helpers.disabledUser")}</Tag>}
          {!hasConfirmedAgentUserAccount(record) && <Tag color="orange">{t("user.helpers.unconfirmedUser")}</Tag>}
          {record.agentUserRole?.systemAdmin && <Tag color="cyan">{t("user.attrs.systemAdmin")}</Tag>}
        </Space>
      ),
    },
    {
      key: "actions",
      align: "right",
      fixed: "right",
      width: 90,
      render: (_, record) => (
        <ActionTextIcon
          path={generatePath(ADMIN_USER_ROUTE_PATHS.detail.to, { id: record.id })}
          icon="eye"
          color="blue"
          text={t("common.show")}
        />
      ),
    },
  ];

  return (
    <Table<UserAdminView>
      {...tableStandardProps()}
      columns={columns}
      loading={isLoading}
      dataSource={data?.pageData}
      scroll={{ x: TableSizes.LARGE }}
      pagination={{
        ...paginationTableProps,
        current: numberOrZero(data?.pageIndex) + 1,
        pageSize: data?.pageSize,
        total: data?.totalElementsCount,
        onChange: (pageIndex) => navigate({ search: URLQuery.getTableChangePageIndex(pageIndex) }, { replace: true }),
      }}
    />
  );
};
